import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { PreviewLayout } from './wysiwyg/types';
import {
  addPreviewLayout,
  editPreviewLayout,
  getPreviewLayoutUsage,
  getPreviewLayouts,
  getPreviewLayoutApiErrorMessage,
  isPreviewLayoutApiError,
  normalizePreviewLayouts,
  normalizePreviewLayoutUsage,
  removePreviewLayout,
  type PreviewLayoutUsageEntry,
} from './wysiwyg/previewLayouts';
import { computeLayout, realToPixels } from './wysiwyg/services/pdfGenerator';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import PreviewLayoutUsageModal from './PreviewLayoutUsageModal';

type LayoutFormState = {
  id: string;
  label: string;
  unit: 'in' | 'cm';
  pageWidth: string;
  pageHeight: string;
  labelWidth: string;
  labelHeight: string;
  marginTop: string;
  marginRight: string;
  marginBottom: string;
  marginLeft: string;
  spacingX: string;
  spacingY: string;
  horizontalPitch: string;
  verticalPitch: string;
  columns: string;
  rows: string;
  printer: string;
  format: 'sheets' | 'roll';
  shape: 'rectangle' | 'round-rectangle' | 'circle';
  cornerRadius: string;
  labelRotation: '0' | '90' | '180' | '270';
};

const emptyForm = (): LayoutFormState => ({
  id: '',
  label: '',
  unit: 'in',
  pageWidth: '8.5',
  pageHeight: '11',
  labelWidth: '4',
  labelHeight: '2',
  marginTop: '0',
  marginRight: '0',
  marginBottom: '0',
  marginLeft: '0',
  spacingX: '0',
  spacingY: '0',
  horizontalPitch: '',
  verticalPitch: '',
  columns: '1',
  rows: '1',
  printer: 'generic',
  format: 'sheets',
  shape: 'rectangle',
  cornerRadius: '',
  labelRotation: '0',
});

const toInputValue = (value?: number) => (value == null ? '' : String(value));

const toFormState = (layout: PreviewLayout): LayoutFormState => ({
  id: layout.id,
  label: layout.label,
  unit: layout.unit,
  pageWidth: String(layout.pageWidth),
  pageHeight: String(layout.pageHeight),
  labelWidth: String(layout.labelWidth),
  labelHeight: String(layout.labelHeight),
  marginTop: String(layout.marginTop),
  marginRight: String(layout.marginRight),
  marginBottom: String(layout.marginBottom),
  marginLeft: String(layout.marginLeft),
  spacingX: String(layout.spacingX),
  spacingY: String(layout.spacingY),
  horizontalPitch: toInputValue(layout.horizontalPitch),
  verticalPitch: toInputValue(layout.verticalPitch),
  columns: toInputValue(layout.columns),
  rows: toInputValue(layout.rows),
  printer: layout.printer || '',
  format: layout.format === 'roll' ? 'roll' : 'sheets',
  shape: layout.shape || 'rectangle',
  cornerRadius: toInputValue(layout.cornerRadius),
  labelRotation: String(layout.labelRotation || 0) as LayoutFormState['labelRotation'],
});

const parseRequiredNumber = (name: string, value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${name} must be a number greater than 0.`);
  }
  return parsed;
};

const parseNonNegativeNumber = (name: string, value: string) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${name} must be 0 or greater.`);
  }
  return parsed;
};

const parseOptionalPositiveNumber = (name: string, value: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${name} must be blank or a number greater than 0.`);
  }
  return parsed;
};

const parseOptionalCount = (name: string, value: string) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${name} must be blank or a whole number greater than 0.`);
  }
  return parsed;
};

const sanitizeLayoutId = (label: string) => label
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const unwrapPreviewLayoutPayload = (payload: unknown) => (
  Array.isArray(payload) ? payload : (payload as any)?.result ?? (payload as any)?.data ?? payload
);

const buildLayout = (form: LayoutFormState): PreviewLayout => {
  const id = form.id;
  const label = form.label;

  const reservedLabels = ['custom', 'Custom', 'Match label size (1-up)', 'Letter 8.5" x 11" (8-up)', 'Letter 8.5" x 11" (1-up)', 'A4 210mm x 297mm (1-up)', 'Label 4" x 6" (roll)', 'Label 2" x 3" (roll)'];
  const reservedIds = ['custom', 'auto-label-size', 'letter-8up', 'letter-single', 'a4-single', 'label4x6-roll', 'label2x3-roll', '__preview__'];

  if (!id) throw new Error('Generated ID is required.');
  if (reservedLabels.includes(label)) throw new Error(`Label "${label}" is reserved. Choose a different label for this layout.`);
  if (reservedIds.includes(id)) throw new Error(`Generated ID "${id}" is reserved. Choose a different label for this layout.`);
  if (!label) throw new Error('Label is required.');

  return {
    id,
    label,
    unit: form.unit,
    pageWidth: parseRequiredNumber('Page width', form.pageWidth),
    pageHeight: parseRequiredNumber('Page height', form.pageHeight),
    labelWidth: parseRequiredNumber('Label width', form.labelWidth),
    labelHeight: parseRequiredNumber('Label height', form.labelHeight),
    marginTop: parseNonNegativeNumber('Top margin', form.marginTop),
    marginRight: parseNonNegativeNumber('Right margin', form.marginRight),
    marginBottom: parseNonNegativeNumber('Bottom margin', form.marginBottom),
    marginLeft: parseNonNegativeNumber('Left margin', form.marginLeft),
    spacingX: parseNonNegativeNumber('Horizontal spacing', form.spacingX),
    spacingY: parseNonNegativeNumber('Vertical spacing', form.spacingY),
    horizontalPitch: parseOptionalPositiveNumber('Horizontal pitch', form.horizontalPitch),
    verticalPitch: parseOptionalPositiveNumber('Vertical pitch', form.verticalPitch),
    columns: parseOptionalCount('Columns', form.columns),
    rows: parseOptionalCount('Rows', form.rows),
    printer: form.printer.trim() || undefined,
    format: form.format,
    shape: form.shape,
    cornerRadius: parseOptionalPositiveNumber('Corner radius', form.cornerRadius),
    labelRotation: form.labelRotation === '0' ? undefined : Number(form.labelRotation) as 90 | 180 | 270,
  };
};

const buildPreviewLayout = (form: LayoutFormState): PreviewLayout => ({
  id: form.id.trim() || '__preview__',
  label: form.label.trim() || 'Preview Layout',
  unit: form.unit,
  pageWidth: parseRequiredNumber('Page width', form.pageWidth),
  pageHeight: parseRequiredNumber('Page height', form.pageHeight),
  labelWidth: parseRequiredNumber('Label width', form.labelWidth),
  labelHeight: parseRequiredNumber('Label height', form.labelHeight),
  marginTop: parseNonNegativeNumber('Top margin', form.marginTop),
  marginRight: parseNonNegativeNumber('Right margin', form.marginRight),
  marginBottom: parseNonNegativeNumber('Bottom margin', form.marginBottom),
  marginLeft: parseNonNegativeNumber('Left margin', form.marginLeft),
  spacingX: parseNonNegativeNumber('Horizontal spacing', form.spacingX),
  spacingY: parseNonNegativeNumber('Vertical spacing', form.spacingY),
  horizontalPitch: parseOptionalPositiveNumber('Horizontal pitch', form.horizontalPitch),
  verticalPitch: parseOptionalPositiveNumber('Vertical pitch', form.verticalPitch),
  columns: parseOptionalCount('Columns', form.columns),
  rows: parseOptionalCount('Rows', form.rows),
  printer: form.printer.trim() || undefined,
  format: form.format,
  shape: form.shape,
  cornerRadius: parseOptionalPositiveNumber('Corner radius', form.cornerRadius),
  labelRotation: form.labelRotation === '0' ? undefined : Number(form.labelRotation) as 90 | 180 | 270,
});

const formatDimension = (value: number, unit: 'in' | 'cm') => `${Number(value.toFixed(3)).toString()} ${unit}`;

function buildLayoutPreviewModel(form: LayoutFormState) {
  try {
    const layout = buildPreviewLayout(form);
    const computed = computeLayout({
      layout,
      labelCount: Number.MAX_SAFE_INTEGER,
      templateWidth: layout.labelWidth,
      templateHeight: layout.labelHeight,
    });

    const labelRotation = layout.labelRotation || 0;
    const previewLabelWidthPx = realToPixels(
      labelRotation === 90 || labelRotation === 270 ? layout.labelHeight : layout.labelWidth,
      layout.unit,
    );
    const previewLabelHeightPx = realToPixels(
      labelRotation === 90 || labelRotation === 270 ? layout.labelWidth : layout.labelHeight,
      layout.unit,
    );

    return {
      layout,
      computed,
      previewLabelWidthPx,
      previewLabelHeightPx,
    };
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : 'Fix validation errors to preview the layout.',
    };
  }
}

export default function PreviewLayoutsAdminPage({ currentUser }: { currentUser?: any }) {
  const navigate = useNavigate();
  const [layouts, setLayouts] = useState<PreviewLayout[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState<LayoutFormState>(emptyForm);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [usageEntries, setUsageEntries] = useState<PreviewLayoutUsageEntry[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageModalLayout, setUsageModalLayout] = useState<PreviewLayout | null>(null);
  const isCreateMode = mode === 'create';
  const canWritePreviewLayouts =
    currentUser?.internalRoleActions?.includes('WriteInternalPreviewLayouts') === true;
  const isReadOnly = !isCreateMode || !canWritePreviewLayouts;

  const selectedLayout = useMemo(
    () => layouts.find((layout) => layout.id === selectedId) || null,
    [layouts, selectedId],
  );

  const filteredLayouts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return layouts;
    }

    return layouts.filter((layout) => (
      layout.label.toLowerCase().includes(normalizedQuery)
      || layout.id.toLowerCase().includes(normalizedQuery)
    ));
  }, [layouts, searchQuery]);
  const layoutPreview = useMemo(() => buildLayoutPreviewModel(form), [form]);

  const applyLayouts = (nextLayouts: PreviewLayout[], nextSelectedId?: string) => {
    setLayouts(nextLayouts);

    const resolvedSelectedId = nextSelectedId ?? nextLayouts[0]?.id ?? '';
    setSelectedId(resolvedSelectedId);

    const nextSelectedLayout = nextLayouts.find((layout) => layout.id === resolvedSelectedId);
    if (nextSelectedLayout) {
      setForm(toFormState(nextSelectedLayout));
      setMode('edit');
    } else {
      setForm(emptyForm());
      setMode('create');
    }
  };

  const loadCurrentLayouts = async (nextSelectedId?: string) => {
    setLoading(true);
    try {
      const payload = await getPreviewLayouts(0, 500);
      if (isPreviewLayoutApiError(payload)) {
        throw new Error(getPreviewLayoutApiErrorMessage(payload));
      }
      const nextLayouts = normalizePreviewLayouts(unwrapPreviewLayoutPayload(payload));
      applyLayouts(nextLayouts, nextSelectedId);
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentLayouts();
  }, []);

  const persistLayout = async (layout: PreviewLayout, persistMode: 'create' | 'edit', successMessage?: string) => {
    setIsSaving(true);

    try {
      if (persistMode === 'create') {
        const result = await addPreviewLayout(layout);
        if (isPreviewLayoutApiError(result)) {
          throw new Error(getPreviewLayoutApiErrorMessage(result) || 'Preview layout could not be created.');
        }
      } else {
        if (!layout.previewLayoutId) {
          throw new Error('Preview layout id is missing. Reload the page and try again.');
        }
        const result = await editPreviewLayout({ previewLayoutId: layout.previewLayoutId, ...layout });
        if (isPreviewLayoutApiError(result)) {
          throw new Error(getPreviewLayoutApiErrorMessage(result) || 'Preview layout could not be updated.');
        }
      }
      await loadCurrentLayouts(layout.id);
      showToastSuccess(successMessage || 'Layout saved.');
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectLayout = (layout: PreviewLayout) => {
    setSelectedId(layout.id);
    setForm(toFormState(layout));
    setMode('edit');
  };

  const handleChange = (field: keyof LayoutFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setForm((prev) => {
        if (field === 'label' && mode === 'create') {
          return {
            ...prev,
            label: value,
            id: sanitizeLayoutId(value),
          };
        }

        return { ...prev, [field]: value };
      });
    };

  const handleCreateNew = () => {
    setSelectedId('');
    setForm(emptyForm());
    setMode('create');
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();

    if (!isCreateMode) {
      return;
    }

    try {
      const nextId = isCreateMode ? sanitizeLayoutId(form.label) : form.id.trim();
      if (isCreateMode && form.label.trim() && !nextId) {
        throw new Error('Label must contain at least one letter or number to generate an ID.');
      }

      const nextLayout = buildLayout({ ...form, id: nextId });
      const duplicate = layouts.find((layout) => layout.id === nextLayout.id);
      if (isCreateMode && duplicate) {
        throw new Error(`A layout with ID "${nextLayout.id}" already exists.`);
      }

      await persistLayout(
        nextLayout,
        mode,
        'Layout created.',
      );
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
    }
  };

  const fetchUsage = async (layout: PreviewLayout) => {
    if (!layout.previewLayoutId) return [];
    const payload = await getPreviewLayoutUsage(layout.previewLayoutId);
    if (isPreviewLayoutApiError(payload)) {
      throw new Error(getPreviewLayoutApiErrorMessage(payload));
    }
    return normalizePreviewLayoutUsage(payload);
  };

  const deleteLayout = async (layout: PreviewLayout) => {
    if (!layout.previewLayoutId) {
      showToastError('Layout id is missing. Reload the page and try again.');
      return;
    }

    setIsSaving(true);
    try {
      const result = await removePreviewLayout(layout.previewLayoutId);
      if (isPreviewLayoutApiError(result)) {
        throw new Error(getPreviewLayoutApiErrorMessage(result));
      }
      await loadCurrentLayouts(layouts.find((candidate) => candidate.id !== layout.id)?.id);
      showToastSuccess(`Deleted "${layout.label}" and saved the preview layout catalog.`);
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLayout) return;
    if (layouts.length <= 1) {
      showToastError('At least one layout must remain. Create another layout before deleting the last one.');
      return;
    }

    setIsSaving(true);
    try {
      const usage = await fetchUsage(selectedLayout);
      if (usage.length) {
        setUsageEntries(usage);
        setUsageModalLayout(selectedLayout);
        return;
      }
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
      return;
    } finally {
      setIsSaving(false);
    }

    if (!window.confirm(`Delete layout "${selectedLayout.label}"?`)) return;
    await deleteLayout(selectedLayout);
  };

  const handleRefreshUsage = async () => {
    if (!usageModalLayout) return;
    setUsageLoading(true);
    try {
      setUsageEntries(await fetchUsage(usageModalLayout));
    } catch (err) {
      showToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setUsageLoading(false);
    }
  };

  const handleCancelUsageModal = () => {
    setUsageModalLayout(null);
    setUsageEntries([]);
  };

  return (
    <div className="stack" data-testid="page-preview-layouts-admin">
      <PageHeader
        title="Preview Layouts"
        subtitle=""
        actions={
          <>
            <button type="button" className="button" onClick={() => navigate('/internal')}>
              Back
            </button>
          </>
        }
      />

      <div className="preview-layout-admin-grid">
        <div className="card preview-layout-list-card">
          <div className="preview-layout-list-toolbar">
            <div>
              <div style={{ fontWeight: 700 }}>Preview Layouts</div>
              <div className="muted">
                {loading
                  ? 'Loading…'
                  : `${filteredLayouts.length} of ${layouts.length} preview layout${layouts.length === 1 ? '' : 's'}`}
              </div>
            </div>
            {canWritePreviewLayouts && (
            <button type="button" className="button cta" onClick={handleCreateNew} disabled={loading || isSaving}>
              New Layout
            </button>
            )}
          </div>
          <input
            className="input preview-layout-list-search"
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search preview layouts by name or ID"
            aria-label="Search preview layouts"
            data-testid="preview-layouts-search"
          />
          <div className="preview-layout-list" data-testid="preview-layouts-list">
            {filteredLayouts.map((layout) => {
              const isActive = layout.id === selectedId;
              return (
                <button
                  key={layout.id}
                  type="button"
                  className={`preview-layout-list-item${isActive ? ' is-active' : ''}`}
                  onClick={() => handleSelectLayout(layout)}
                >
                  <span className="preview-layout-list-item__title">{layout.label}</span>
                  <span className="preview-layout-list-item__meta">{layout.id}</span>
                </button>
              );
            })}
            {!loading && !layouts.length && (
              <div className="muted" style={{ padding: '0.75rem' }}>No preview layouts loaded.</div>
            )}
            {!loading && !!layouts.length && !filteredLayouts.length && (
              <div className="muted" style={{ padding: '0.75rem' }}>No preview layouts match your search.</div>
            )}
          </div>
        </div>

        <form className="card preview-layout-form-card" onSubmit={handleSave}>
          <div className="preview-layout-form-toolbar">
            <div>
              <div style={{ fontWeight: 700 }}>{isCreateMode ? 'Create Preview Layout' : 'Preview Layout Details'}</div>
              <div className="muted">
                {isCreateMode
                  ? 'Set dimensions, spacing, printer hints, and optional pitch values for a new layout.'
                  : 'Current layout values are read only. Use New Layout to add another layout.'}
              </div>
            </div>
            {isCreateMode && canWritePreviewLayouts && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="button cta" disabled={loading || isSaving} data-testid="preview-layouts-save">
                  {isSaving ? 'Saving…' : 'Create'}
                </button>
              </div>
            )}
            {!isCreateMode && currentUser?.internalRoleActions?.includes('AdminInternalPreviewLayouts') === true && (
              <button
                type="button"
                className="button"
                onClick={handleDelete}
                disabled={loading || isSaving || !selectedLayout}
                data-testid="preview-layouts-delete"
              >
                Delete
              </button>
            )}
          </div>

          <div className="preview-layout-form-grid">
            <label className="preview-layout-field">
              <span>Label</span>
              <input className="input" value={form.label} onChange={handleChange('label')} placeholder="Avery 5163" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Unit</span>
              <select className="input" value={form.unit} onChange={handleChange('unit')} disabled={isReadOnly}>
                <option value="in">in</option>
                <option value="cm">cm</option>
              </select>
            </label>
            <label className="preview-layout-field">
              <span>Format</span>
              <select className="input" value={form.format} onChange={handleChange('format')} disabled={isReadOnly}>
                <option value="sheets">sheets</option>
                <option value="roll">roll</option>
              </select>
            </label>
            <label className="preview-layout-field">
              <span>Shape</span>
              <select className="input" value={form.shape} onChange={handleChange('shape')} disabled={isReadOnly}>
                <option value="rectangle">rectangle</option>
                <option value="round-rectangle">round-rectangle</option>
                <option value="circle">circle</option>
              </select>
            </label>

            <label className="preview-layout-field">
              <span>Page Width</span>
              <input className="input" type="number" step="any" value={form.pageWidth} onChange={handleChange('pageWidth')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Page Height</span>
              <input className="input" type="number" step="any" value={form.pageHeight} onChange={handleChange('pageHeight')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Label Width (Length)</span>
              <input className="input" type="number" step="any" value={form.labelWidth} onChange={handleChange('labelWidth')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Label Height</span>
              <input className="input" type="number" step="any" value={form.labelHeight} onChange={handleChange('labelHeight')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Top Margin</span>
              <input className="input" type="number" step="any" value={form.marginTop} onChange={handleChange('marginTop')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Bottom Margin</span>
              <input className="input" type="number" step="any" value={form.marginBottom} onChange={handleChange('marginBottom')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Left Margin</span>
              <input className="input" type="number" step="any" value={form.marginLeft} onChange={handleChange('marginLeft')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Right Margin</span>
              <input className="input" type="number" step="any" value={form.marginRight} onChange={handleChange('marginRight')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Horizontal Spacing</span>
              <input className="input" type="number" step="any" value={form.spacingX} onChange={handleChange('spacingX')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Vertical Spacing</span>
              <input className="input" type="number" step="any" value={form.spacingY} onChange={handleChange('spacingY')} readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Horizontal Pitch</span>
              <input className="input" type="number" step="any" value={form.horizontalPitch} onChange={handleChange('horizontalPitch')} placeholder="optional" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Vertical Pitch</span>
              <input className="input" type="number" step="any" value={form.verticalPitch} onChange={handleChange('verticalPitch')} placeholder="optional" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Columns</span>
              <input className="input" type="number" step="1" value={form.columns} onChange={handleChange('columns')} placeholder="optional" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Rows</span>
              <input className="input" type="number" step="1" value={form.rows} onChange={handleChange('rows')} placeholder="optional" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Printer</span>
              <input className="input" value={form.printer} onChange={handleChange('printer')} placeholder="generic" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Corner radius</span>
              <input className="input" type="number" step="any" value={form.cornerRadius} onChange={handleChange('cornerRadius')} placeholder="optional" readOnly={isReadOnly} />
            </label>
            <label className="preview-layout-field">
              <span>Label rotation</span>
              <select className="input" value={form.labelRotation} onChange={handleChange('labelRotation')} disabled={isReadOnly}>
                <option value="0">0</option>
                <option value="90">90</option>
                <option value="180">180</option>
                <option value="270">270</option>
              </select>
            </label>
          </div>

          <div className="preview-layout-visual-preview">
            <div className="preview-layout-visual-preview__header">
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginBottom: '0.25rem' }}>
                  Print Preview
                </div>
                <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>
                  {layoutPreview.error
                    ? 'Preview unavailable'
                    : (form.label.trim() || `${formatDimension(layoutPreview.layout.pageWidth, layoutPreview.layout.unit)} x ${formatDimension(layoutPreview.layout.pageHeight, layoutPreview.layout.unit)}`)}
                </div>
                {!layoutPreview.error && (
                  <div className="muted" style={{ fontSize: '0.85rem' }}>
                    Labels/page: {layoutPreview.computed.labelsPerPage} · Pages: 1
                  </div>
                )}
              </div>
              {!layoutPreview.error && (
                <div className="preview-layout-visual-preview__summary">
                  <span>{formatDimension(layoutPreview.layout.pageWidth, layoutPreview.layout.unit)} x {formatDimension(layoutPreview.layout.pageHeight, layoutPreview.layout.unit)}</span>
                  <span>{layoutPreview.layout.format === 'roll' ? 'Roll' : 'Sheet'} layout</span>
                </div>
              )}
            </div>

            {layoutPreview.error ? (
              <div className="preview-layout-visual-preview__empty">
                <div style={{ fontSize: '0.95rem', marginBottom: '0.35rem' }}>No preview available</div>
                <div>{layoutPreview.error}</div>
              </div>
            ) : (() => {
              const maxPreviewWidth = 420;
              const maxPreviewHeight = 420;
              const previewScale = Math.min(
                1.6,
                maxPreviewWidth / layoutPreview.computed.pageWidthPx,
                maxPreviewHeight / layoutPreview.computed.pageHeightPx,
              );
              const scaledPageWidth = Math.max(72, layoutPreview.computed.pageWidthPx * previewScale);
              const scaledPageHeight = Math.max(72, layoutPreview.computed.pageHeightPx * previewScale);
              const slotBorderRadius = layoutPreview.layout.shape === 'circle'
                ? '999px'
                : `${Math.max(
                  6,
                  realToPixels(layoutPreview.layout.cornerRadius || (layoutPreview.layout.shape === 'round-rectangle' ? 0.125 : 0), layoutPreview.layout.unit) * previewScale,
                )}px`;

              return (
                <>
                  <div className={`preview-layout-visual-preview__stage${layoutPreview.layout.format === 'roll' ? ' is-roll' : ''}`}>
                    <div
                      className="preview-layout-visual-preview__page"
                      style={{
                        width: `${scaledPageWidth}px`,
                        height: `${scaledPageHeight}px`,
                        borderRadius: layoutPreview.layout.format === 'roll' ? '24px' : '16px',
                      }}
                    >
                      {layoutPreview.computed.positions.slice(0, 500).map((position, index) => (
                        <div
                          key={`${position.x}-${position.y}-${index}`}
                          className={`preview-layout-slot preview-layout-slot--${layoutPreview.layout.shape || 'rectangle'}`}
                          style={{
                            left: `${position.x * previewScale}px`,
                            top: `${position.y * previewScale}px`,
                            width: `${layoutPreview.previewLabelWidthPx * previewScale}px`,
                            height: `${layoutPreview.previewLabelHeightPx * previewScale}px`,
                            borderRadius: slotBorderRadius,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="preview-layout-visual-preview__meta">
                    <span>Label: {formatDimension(layoutPreview.layout.labelWidth, layoutPreview.layout.unit)} x {formatDimension(layoutPreview.layout.labelHeight, layoutPreview.layout.unit)}</span>
                    <span>Margins: T {formatDimension(layoutPreview.layout.marginTop, layoutPreview.layout.unit)} / R {formatDimension(layoutPreview.layout.marginRight, layoutPreview.layout.unit)} / B {formatDimension(layoutPreview.layout.marginBottom, layoutPreview.layout.unit)} / L {formatDimension(layoutPreview.layout.marginLeft, layoutPreview.layout.unit)}</span>
                    <span>Spacing: {formatDimension(layoutPreview.layout.spacingX, layoutPreview.layout.unit)} x {formatDimension(layoutPreview.layout.spacingY, layoutPreview.layout.unit)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </form>
      </div>

      <PreviewLayoutUsageModal
        show={!!usageModalLayout}
        layoutLabel={usageModalLayout?.label || ''}
        entries={usageEntries}
        loading={usageLoading}
        onRefresh={handleRefreshUsage}
        onCancel={handleCancelUsageModal}
      />
    </div>
  );
}
